import { MuiAlert, Select, strIsNullOrEmpty, tryParseJSONObject, useDebounce } from '@fox/atoms';
import React, { useMemo, useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useEntitySearch } from '../../query-hooks/query-implementations';
import { CustomFieldEditProps, SwiftFoxSearchResult } from '../contracts';

interface ILinkMeta {
	singleSelect?: boolean;
	allowCreate?: boolean;
	types: string[];
	fullName?: boolean;
	categories?: string[];
}

export const LinkCustomField: React.FC<CustomFieldEditProps> = ({ customField, onChange }) => {
	const meta = tryParseJSONObject<ILinkMeta>(customField.meta);
	const [search, setSearch] = useState<string>('');
	const debouncedSearch = useDebounce(search, 1000);
	const { contentId, requestToken } = useContentContext();
	const { data, error, loading } = useEntitySearch({
		contentId,
		requestToken,
		types: meta.types ?? [],
		searchString: debouncedSearch,
		triggers: ['searchString']
	});

	const value = useMemo(() => {
		return !meta.singleSelect ? customField.value ?? [] : customField.value?.[0] ?? [];
	}, [customField.value]);

	if (error) return <MuiAlert severity="error">Unable to load options</MuiAlert>;

	return (
		<Select
			loading={loading}
			multiple={!meta.singleSelect}
			label={customField.displayName}
			options={
				data?.map(d => ({
					entity: { id: d.entity.id, type: d.entity.type },
					name: d.name,
					extraDetails: d.extraDetails,
					image: d.image
				})) ?? []
			}
			value={value}
			getOptionLabel={(o: SwiftFoxSearchResult) => o?.name ?? ''}
			onInputChange={(_, value) => {
				setSearch(value);
			}}
			inputValue={search}
			isOptionEqualToValue={(option, value) => {
				return option?.entity?.id === value?.entity?.id && option?.entity?.type === value?.entity?.type;
			}}
			onChange={(_, v) => {
				let newValue;
				if (Array.isArray(v)) {
					const result = [];
					const map = new Map();
					for (const item of v) {
						if (!map.has(item.entity.id)) {
							map.set(item.entity.id, true);
							result.push(item);
						}
					}
					newValue = result;
				} else {
					newValue = !strIsNullOrEmpty(v) ? [v] : [];
				}
				onChange(customField.customFieldId, newValue);
			}}
		/>
	);
};
